import JoditEditor from 'jodit-react';
import '../../../Styles/TextEditor.css';
import React from 'react';
import { MdCheckCircle, MdClose, MdCloudUpload, MdCompress, MdErrorOutline, MdOpenInNew, MdPlayArrow, MdSwapHoriz } from 'react-icons/md';
import { useAuth } from '../../../Context/AuthContext';
import { getAegisAiDocumentMedia, getAegisAiYoutubePreview } from '../../../Services/AegisAiDocumentsService';
import { AI_SOURCE_MAX_BYTES, AI_SOURCE_TYPES, formatFileSize, getAiSourceType, getFileMeta } from '../AegisAiUi';

const maxSizeLabel = formatFileSize(AI_SOURCE_MAX_BYTES);
// Matches the API's whole-site page cap.
const WHOLE_SITE_MAX_PAGES = 25;

const getBaseName = (path) => String(path || '').split(/[\\/]/).filter(Boolean).pop() || '';

const getExtension = (name) => (String(name || '').match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();

const isMediaType = (key) => key === 'audio' || key === 'video';

const getYoutubeId = (url) => {
    const match = String(url || '').match(/(?:youtu\.be\/|[?&]v=|\/(?:shorts|live|embed)\/)([\w-]{6,})/i);
    return match ? match[1] : null;
};

const getHostname = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
};


// Object URL for a Blob/File that is revoked when the blob changes or the component unmounts.
function useObjectUrl(blob) {
    const [url, setUrl] = React.useState(null);
    React.useEffect(() => {
        if (!blob) {
            setUrl(null);
            return undefined;
        }
        const objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [blob]);
    return url;
}

function SourceTypeCards({ value, onChange, disabled }) {
    return (
        <>
            <div className="form-label fw-semibold" id="hc-ai-src-type">Source type <span className="text-danger">*</span></div>
            <div className="hc-ai-src-types mb-3" role="radiogroup" aria-labelledby="hc-ai-src-type">
                {AI_SOURCE_TYPES.map(({ key, label, caption, Icon, color }) => (
                    <label
                        key={key}
                        className={`hc-ai-src-type d-flex flex-column align-items-center text-center rounded-3 ${key === value ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
                        style={{ '--src-color': color }}
                        title={caption}
                    >
                        <input
                            type="radio"
                            name="hc-ai-src-type"
                            className="visually-hidden"
                            value={key}
                            checked={key === value}
                            onChange={() => onChange(key)}
                            disabled={disabled}
                        />
                        <MdCheckCircle size={16} className="hc-ai-src-type-check" aria-hidden="true" />
                        <span className="hc-ai-src-type-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-1">
                            <Icon size={20} />
                        </span>
                        <span className="hc-ai-src-type-label fw-bold">{label}</span>
                        <span className="hc-ai-src-type-caption text-truncate w-100">{caption}</span>
                    </label>
                ))}
            </div>
        </>
    );
}

function SelectedFileCard({ name, detail, badge, onRemove, onReplace, disabled, children }) {
    const meta = getFileMeta(name);
    const FileIcon = meta.Icon;
    return (
        <div className="hc-ai-src-file rounded-4">
            <div className="d-flex align-items-center gap-3 p-3">
                <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': meta.color }}>
                    <FileIcon size={24} />
                </span>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="hc-ai-file-name fw-bold text-truncate" title={name}>{name}</span>
                        {badge ? <span className="hc-ai-src-badge rounded-pill fw-bold text-uppercase">{badge}</span> : null}
                    </div>
                    <div className="hc-ai-file-meta d-flex align-items-center gap-1">
                        <MdCheckCircle size={14} className="hc-ai-src-ok flex-shrink-0" />
                        <span className="text-truncate">{[meta.ext ? `${meta.ext} file` : 'File', detail].filter(Boolean).join(' · ')}</span>
                    </div>
                </div>
                {onReplace ? (
                    <button type="button" className="hc-ai-src-icon-btn border-0 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" title="Replace file" aria-label="Replace file" onClick={onReplace} disabled={disabled}>
                        <MdSwapHoriz size={18} />
                    </button>
                ) : null}
                <button type="button" className="hc-ai-src-icon-btn is-danger border-0 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" title="Remove file" aria-label={`Remove ${name}`} onClick={onRemove} disabled={disabled}>
                    <MdClose size={18} />
                </button>
            </div>
            {children}
        </div>
    );
}

function LocalMediaPreview({ file, poster, kind }) {
    const url = useObjectUrl(file);
    const posterUrl = useObjectUrl(poster);
    if (!url) return null;
    return (
        <div className="hc-ai-src-media px-3 pb-3">
            {kind === 'audio' ? <audio src={url} controls className="w-100" /> : <video src={url} poster={posterUrl || undefined} controls className="w-100 rounded-3" />}
        </div>
    );
}

// A just-saved recording that the API is still compressing and uploading in the background.
function MediaProcessingNotice({ kind }) {
    return (
        <div className="px-3 pb-3">
            <div className="d-flex align-items-center gap-2 rounded-3 border p-3" role="status">
                <span className="spinner-border spinner-border-sm flex-shrink-0" aria-hidden="true" />
                <div>
                    <div className="fw-bold">This {kind} is still being compressed</div>
                    <div className="small text-muted">It is being compressed and uploaded in the background and can be played here once that finishes. Its content is already being indexed.</div>
                </div>
            </div>
        </div>
    );
}

// Media already saved with the document: the thumbnail loads right away, the recording only on Play.
function SavedMediaPreview({ documentId, kind, hasThumbnail }) {
    const { token } = useAuth();
    const [thumbnail, setThumbnail] = React.useState(null);
    const [media, setMedia] = React.useState(null);
    const [status, setStatus] = React.useState('idle');
    const thumbnailUrl = useObjectUrl(thumbnail);
    const mediaUrl = useObjectUrl(media);
    const playAbortRef = React.useRef(null);

    React.useEffect(() => {
        if (!hasThumbnail || !token) return undefined;
        const controller = new AbortController();
        getAegisAiDocumentMedia(documentId, 'thumbnail', token, controller.signal)
            .then(setThumbnail)
            .catch(() => setThumbnail(null));
        return () => controller.abort();
    }, [documentId, hasThumbnail, token]);

    React.useEffect(() => () => playAbortRef.current?.abort(), []);

    const play = async () => {
        playAbortRef.current?.abort();
        const controller = new AbortController();
        playAbortRef.current = controller;
        setStatus('loading');
        try {
            setMedia(await getAegisAiDocumentMedia(documentId, 'media', token, controller.signal));
            setStatus('idle');
        } catch (error) {
            if (error.name !== 'AbortError') setStatus('error');
        }
    };

    if (mediaUrl) {
        return (
            <div className="hc-ai-src-media px-3 pb-3">
                {kind === 'audio'
                    ? <audio src={mediaUrl} controls autoPlay className="w-100" />
                    : <video src={mediaUrl} poster={thumbnailUrl || undefined} controls autoPlay className="w-100 rounded-3" />}
            </div>
        );
    }
    return (
        <div className="px-3 pb-3">
            <button
                type="button"
                className={`hc-ai-src-saved-media w-100 border-0 rounded-3 d-flex align-items-center justify-content-center ${kind === 'audio' ? 'is-audio' : ''}`}
                style={thumbnailUrl ? { backgroundImage: `url(${thumbnailUrl})` } : undefined}
                onClick={play}
                disabled={status === 'loading'}
                aria-label={kind === 'audio' ? 'Play audio' : 'Play video'}
            >
                <span className="hc-ai-src-play d-inline-flex align-items-center justify-content-center rounded-circle">
                    {status === 'loading' ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : <MdPlayArrow size={28} />}
                </span>
            </button>
            {status === 'error' ? (
                <div className="hc-ai-src-error d-flex align-items-center gap-1 mt-2">
                    <MdErrorOutline size={15} className="flex-shrink-0" />
                    <span>Could not load the saved {kind}. Please try again.</span>
                </div>
            ) : null}
        </div>
    );
}

// Title/channel for a pasted YouTube link, fetched as soon as the link is valid (before saving).
function useYoutubePreview(youtubeId, url) {
    const { token } = useAuth();
    const [preview, setPreview] = React.useState({ id: null, data: null, error: '' });
    React.useEffect(() => {
        if (!youtubeId || !token) return undefined;
        const controller = new AbortController();
        // Short debounce so typing/pasting doesn't fire a request per keystroke.
        const timer = setTimeout(async () => {
            try {
                const response = await getAegisAiYoutubePreview(url, token, controller.signal);
                setPreview(response?.is_success
                    ? { id: youtubeId, data: response.data, error: '' }
                    : { id: youtubeId, data: null, error: response?.message || 'Video not found or is private' });
            } catch (error) {
                if (error.name !== 'AbortError') setPreview({ id: youtubeId, data: null, error: '' });
            }
        }, 250);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [youtubeId, url, token]);
    return preview.id === youtubeId ? preview : { data: null, error: '' };
}

const CUSTOM_TEXT_JODIT_CONFIG = {
    readonly: false,
    placeholder: "Type or paste custom text or notes to feed into RAG knowledge...",
    hidePoweredByJodit: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_only_text',
    defaultActionOnPasteFromWord: 'insert_only_text',
    processPasteHTML: false,
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: false,
    height: 380,
    minHeight: 280,
    removeButtons: [
        "link", "table", "hr", "symbol", "math", "image", "video", "file",
        "speechRecognize", "print", "about", "fullsize", "preview", "classSpan",
        "superscript", "subscript", "lineHeight", "font", "copyformat"
    ],
    disablePlugins: [
        "about", "powered-by-jodit", "poweredByJodit", "symbol", "math", "image", "video", "file", "link", "table"
    ],
    buttons: [
        "bold", "italic", "underline", "strikethrough", "eraser", "|",
        "ul", "ol", "|",
        "paragraph", "fontsize", "|",
        "brush", "|",
        "left", "center", "right", "justify", "|",
        "undo", "redo", "|",
        "cut", "copy", "paste", "selectall", "|",
        "find", "source"
    ],
};

export default function AiSourcePicker({
    sourceType,
    onSourceTypeChange,
    file,
    fileInfo,
    existingFileName,
    existingMedia,
    onFileChange,
    onRemoveExisting,
    onCompressingChange,
    url,
    onUrlChange,
    onUrlBlur,
    text,
    onTextChange,
    wholeSite = false,
    onWholeSiteChange,
    error,
    disabled = false,
    isEdit = false,
    renderBeforeFile = null,
}) {
    const inputRef = React.useRef(null);
    const compressionRef = React.useRef(null);
    const [dragging, setDragging] = React.useState(false);
    const [localError, setLocalError] = React.useState('');
    const [oversizeError, setOversizeError] = React.useState('');
    const [compressing, setCompressing] = React.useState(null);
    const type = getAiSourceType(sourceType);
    const accept = type.extensions ? type.extensions.map((ext) => `.${ext}`).join(',') : undefined;

    const cancelCompression = React.useCallback(() => {
        compressionRef.current?.abort();
        compressionRef.current = null;
        setCompressing(null);
        onCompressingChange?.(false);
    }, [onCompressingChange]);

    // A new source type starts clean.
    React.useEffect(() => {
        setLocalError('');
        setOversizeError('');
    }, [sourceType]);

    // Leaving the page abandons an in-flight compression.
    React.useEffect(() => () => compressionRef.current?.abort(), []);

    const pickFile = async (candidate) => {
        if (!candidate) return;
        if (!type.extensions.includes(getExtension(candidate.name))) {
            setOversizeError('');
            setLocalError(`"${candidate.name}" isn't a supported ${type.label.toLowerCase()} file. Allowed: ${type.caption}.`);
            return;
        }
        // Checked here so an oversized file is rejected before anything is compressed or uploaded.
        if (candidate.size > AI_SOURCE_MAX_BYTES) {
            setLocalError('');
            setOversizeError(`"${candidate.name}" is ${formatFileSize(candidate.size)}, which exceeds the ${maxSizeLabel} limit for ${type.label.toLowerCase()} files. Please choose a smaller file.`);
            return;
        }
        setLocalError('');
        setOversizeError('');
        // Audio/video are compressed by the API in the background after saving, so the pick is handed over
        // as-is and extraction starts right away.
        onFileChange(candidate, null);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        if (disabled || compressing) return;
        pickFile(event.dataTransfer.files?.[0]);
    };

    const handleInput = (event) => {
        pickFile(event.currentTarget.files?.[0]);
        // Reset so choosing the same file again after removing it still fires onChange.
        event.currentTarget.value = '';
    };

    const browse = () => inputRef.current?.click();
    // The old pick is cleared while its replacement compresses, which isn't a missing-file error.
    const shownError = localError || (compressing ? '' : error);
    const trimmedUrl = String(url || '').trim();
    const urlIsValid = type.kind === 'url' && trimmedUrl && type.pattern.test(trimmedUrl);
    const youtubeId = type.key === 'youtube' && urlIsValid ? getYoutubeId(trimmedUrl) : null;
    const youtubePreview = useYoutubePreview(youtubeId, trimmedUrl);

    const joditRef = React.useRef(null);
    const isFirstLoad = React.useRef(true);
    const [editorValue, setEditorValue] = React.useState(text || '');

    // Sync only on initial async load of text (e.g. edit mode)
    React.useEffect(() => {
        if (isFirstLoad.current && text) {
            isFirstLoad.current = false;
            setEditorValue(text);
        }
    }, [text]);

    const newFileDetail = !file ? ''
        : fileInfo?.compressed ? `${formatFileSize(file.size)} · compressed from ${formatFileSize(fileInfo.originalSize)}`
        : `${formatFileSize(file.size)} of ${maxSizeLabel} max`;

    return (
        <div className="hc-ai-src">
            <SourceTypeCards value={type.key} onChange={onSourceTypeChange} disabled={disabled || Boolean(compressing)} />

            {renderBeforeFile ? renderBeforeFile() : null}

            {type.kind === 'file' ? (
                <>
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <label className="form-label fw-semibold mb-0">{type.label} file <span className="text-danger">*</span></label>
                        <span className={`hc-ai-src-limit rounded-pill fw-bold d-inline-flex align-items-center gap-1 ${oversizeError ? 'is-exceeded' : ''}`}>
                            {oversizeError ? <MdErrorOutline size={13} /> : null}
                            {oversizeError ? `Exceeds ${maxSizeLabel} limit` : `Max ${maxSizeLabel}`}
                        </span>
                    </div>
                    {compressing ? (
                        <div className="hc-ai-src-file rounded-4 p-3">
                            <div className="d-flex align-items-center gap-3">
                                <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': type.color }}>
                                    <MdCompress size={22} />
                                </span>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <div className="hc-ai-file-name fw-bold text-truncate" title={compressing.name}>{compressing.name}</div>
                                    <div className="hc-ai-file-meta">
                                        Compressing {formatFileSize(compressing.size)} before upload… {Math.round(compressing.progress * 100)}%
                                    </div>
                                </div>
                                <button type="button" className="hc-ai-src-icon-btn is-danger border-0 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" title="Cancel" aria-label="Cancel compression" onClick={cancelCompression}>
                                    <MdClose size={18} />
                                </button>
                            </div>
                            <div className="progress mt-3" style={{ height: 6 }} role="progressbar" aria-label="Compression progress" aria-valuenow={Math.round(compressing.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                                <div className="progress-bar" style={{ width: `${Math.round(compressing.progress * 100)}%`, background: type.color }} />
                            </div>
                        </div>
                    ) : file ? (
                        <SelectedFileCard name={file.name} detail={newFileDetail} badge="New" onRemove={() => onFileChange(null, null)} onReplace={browse} disabled={disabled}>
                            {isMediaType(type.key) ? <LocalMediaPreview file={file} poster={fileInfo?.thumbnail} kind={type.key} /> : null}
                        </SelectedFileCard>
                    ) : existingFileName ? (
                        <SelectedFileCard name={getBaseName(existingFileName)} badge="Current" onRemove={onRemoveExisting} onReplace={browse} disabled={disabled}>
                            {isMediaType(type.key) && existingMedia?.processing
                                ? <MediaProcessingNotice kind={type.key} />
                                : isMediaType(type.key) && existingMedia?.documentId
                                ? <SavedMediaPreview documentId={existingMedia.documentId} kind={type.key} hasThumbnail={existingMedia.hasThumbnail} />
                                : null}
                        </SelectedFileCard>
                    ) : (
                        <div
                            role="button"
                            tabIndex={0}
                            className={`hc-ai-src-drop d-flex flex-column align-items-center justify-content-center text-center rounded-4 ${dragging ? 'is-dragging' : ''} ${shownError || oversizeError ? 'is-invalid' : ''}`}
                            style={{ '--src-color': type.color }}
                            onClick={browse}
                            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); browse(); } }}
                            onDragOver={(event) => { event.preventDefault(); if (!dragging) setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                        >
                            <span className="hc-ai-src-drop-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-2">
                                <MdCloudUpload size={26} />
                            </span>
                            <div className="fw-bold">
                                {dragging ? `Drop your ${type.label.toLowerCase()} file` : <>Drag &amp; drop a {type.label.toLowerCase()} file, or <span className="hc-ai-src-link">browse</span></>}
                            </div>
                            <div className="hc-ai-src-drop-hint">Supported: {type.caption}</div>
                            {/* Compression is disabled for now; see pickFile. */}
                            {/* {isMediaType(type.key) ? <div className="hc-ai-src-drop-hint">Compressed in your browser before upload</div> : null} */}
                        </div>
                    )}
                    {oversizeError ? (
                        <div className="hc-ai-src-alert d-flex align-items-start gap-2 rounded-3 mt-2" role="alert">
                            <MdErrorOutline size={18} className="flex-shrink-0 mt-1" />
                            <div>
                                <div className="fw-bold">File size limit exceeded</div>
                                <div>{oversizeError}</div>
                            </div>
                        </div>
                    ) : null}
                    <input ref={inputRef} type="file" accept={accept} className="d-none" onChange={handleInput} />
                </>
            ) : type.kind === 'text' ? (
                isEdit ? null : (
                    <>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <label className="form-label fw-semibold mb-0" htmlFor="hc-ai-src-text">{type.label} Editor <span className="text-danger">*</span></label>
                        </div>
                        <div className="hc-ai-jodit-picker-wrap rounded-3 border">
                            <JoditEditor
                                ref={joditRef}
                                value={editorValue}
                                config={CUSTOM_TEXT_JODIT_CONFIG}
                                onBlur={(newContent) => {
                                    if (onTextChange) onTextChange(newContent);
                                }}
                                onChange={() => {}}
                            />
                        </div>
                        <div className="d-flex justify-content-between align-items-center pt-2 mt-1 text-muted small">
                            <span>Direct editor input will be vectorized into RAG knowledge</span>
                        </div>
                    </>
                )
            ) : (
                <>
                    <label className="form-label fw-semibold" htmlFor="hc-ai-src-url">{type.label} URL <span className="text-danger">*</span></label>
                    <div className={`hc-ai-src-url d-flex align-items-center rounded-3 ${shownError ? 'is-invalid' : ''}`} style={{ '--src-color': type.color }}>
                        <span className="hc-ai-src-url-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                            <type.Icon size={18} />
                        </span>
                        <input
                            id="hc-ai-src-url"
                            type="url"
                            inputMode="url"
                            className="flex-grow-1 border-0 bg-transparent"
                            placeholder={type.placeholder}
                            value={url}
                            onChange={(event) => onUrlChange(event.target.value)}
                            onBlur={onUrlBlur}
                            disabled={disabled}
                            autoComplete="off"
                        />
                        {url ? (
                            <button type="button" className="hc-ai-src-icon-btn is-danger border-0 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 me-1" aria-label="Clear URL" onClick={() => onUrlChange('')} disabled={disabled}>
                                <MdClose size={16} />
                            </button>
                        ) : null}
                    </div>
                    {urlIsValid ? (
                        <div className="hc-ai-src-file rounded-4 mt-3 d-flex align-items-center gap-3 p-3">
                            {youtubeId ? (
                                <img className="hc-ai-src-thumb rounded-3 flex-shrink-0" src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`} alt="YouTube thumbnail" />
                            ) : (
                                <span className="hc-ai-file-icon d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white" style={{ '--file-color': type.color }}>
                                    <type.Icon size={22} />
                                </span>
                            )}
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="hc-ai-file-name fw-bold text-truncate" title={youtubePreview.data?.Title}>
                                    {youtubePreview.data?.Title || getHostname(trimmedUrl) || trimmedUrl}
                                </div>
                                <div className="hc-ai-file-meta text-truncate" title={trimmedUrl}>
                                    {youtubePreview.error || youtubePreview.data?.AuthorName || trimmedUrl}
                                </div>
                            </div>
                            <a className="hc-ai-src-icon-btn rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" href={trimmedUrl} target="_blank" rel="noopener noreferrer" title="Open link" aria-label="Open link">
                                <MdOpenInNew size={17} />
                            </a>
                        </div>
                    ) : null}
                    {type.key === 'website' ? (
                        <div className="form-check form-switch mt-3 mb-0">
                            <input
                                id="hc-ai-src-whole-site"
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={wholeSite}
                                onChange={(event) => onWholeSiteChange?.(event.target.checked)}
                                disabled={disabled}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="hc-ai-src-whole-site">Whole site</label>
                            <div className="hc-ai-src-drop-hint">
                                Also reads up to {WHOLE_SITE_MAX_PAGES} of the site's most useful pages (services, locations, providers, FAQs...). You can untick pages in the review.
                            </div>
                        </div>
                    ) : null}
                </>
            )}

            {shownError ? (
                <div className="hc-ai-src-error d-flex align-items-center gap-1 mt-2">
                    <MdErrorOutline size={15} className="flex-shrink-0" />
                    <span>{shownError}</span>
                </div>
            ) : null}
        </div>
    );
}

